import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Users, DollarSign, AlertCircle, TrendingUp, CheckCircle2, Bell,
  BarChart3, Brain, Smartphone, Crown, Sparkles, ShieldCheck, ArrowRight,
} from "lucide-react";
import dashboardAnaliticoImg from "@/assets/dashboard-analitico.png";
import centralInteligenteImg from "@/assets/central-inteligente.png";

type Stats = {
  totalClients: number;
  totalEmprestado: number;
  ativos: number;
  finalizados: number;
  vencidos: number;
  juros: number;
  totalContratos: number;
};

const fmtBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const fmtNum = (v: number) => v.toLocaleString("pt-BR");

const Landing = () => {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    document.title = "Pare de perder dinheiro com empréstimos | Gestão Inteligente MV";
    const meta =
      document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute(
      "content",
      "Sistema profissional de gestão de empréstimos: controle clientes, cobranças, lucros e atrasos com dados reais. Teste agora.",
    );
    document.head.appendChild(meta);

    supabase.functions
      .invoke("public-stats")
      .then(({ data }) => {
        if (data && !data.error) setStats(data as Stats);
      })
      .catch(() => {});
  }, []);

  const s = stats ?? {
    totalClients: 0, totalEmprestado: 0, ativos: 0,
    finalizados: 0, vencidos: 0, juros: 0, totalContratos: 0,
  };

  const benefits = [
    { icon: Users, title: "Saiba quem deve", desc: "Lista clara de devedores com status atualizado." },
    { icon: DollarSign, title: "Veja seu lucro real", desc: "Cálculo automático de juros, parcelas e patrimônio." },
    { icon: Bell, title: "Alertas automáticos", desc: "Vencimentos, atrasos e cobranças sem esquecer." },
    { icon: Brain, title: "Score de clientes", desc: "Identifique bons pagadores e reduza riscos." },
    { icon: Sparkles, title: "Remarketing inteligente", desc: "Reative clientes que já quitaram dívidas." },
    { icon: BarChart3, title: "Cobrança organizada", desc: "Tudo num só painel: dashboard, relatórios e alertas." },
    { icon: Smartphone, title: "Gestão no celular", desc: "100% mobile. Use de qualquer lugar, na hora." },
    { icon: ShieldCheck, title: "Dados protegidos", desc: "Seu negócio com segurança de nível bancário." },
  ];

  const faqs = [
    { q: "Funciona no celular?", a: "Sim. O sistema é 100% otimizado para celular, tablet e computador." },
    { q: "Preciso instalar algo?", a: "Não. É totalmente online. Basta acessar com seu login." },
    { q: "Posso cancelar quando quiser?", a: "Sim. Sem fidelidade. Cancele direto no painel a qualquer momento." },
    { q: "Meus dados ficam salvos?", a: "Sim. Tudo armazenado em nuvem segura, com backup automático." },
    { q: "Serve para operação pequena?", a: "Sim. Funciona desde 1 cliente até centenas. Cresce com você." },
  ];

  return (
    <div className="min-h-screen text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/60 border-b border-white/[0.05]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/lp" className="flex items-center gap-2 font-bold">
            <Crown className="w-5 h-5 text-gold" />
            <span className="text-gradient-primary">Gestão Inteligente MV</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/auth"><Button variant="ghost" size="sm">Entrar</Button></Link>
            <Link to="/auth"><Button variant="premium" size="sm">Quero Testar</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container mx-auto px-4 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs mb-6">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            Sistema usado por gestores reais — dados ao vivo abaixo
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-5 leading-[1.05]">
            Pare de controlar empréstimos no caderno, no WhatsApp e na memória.<br />
            <span className="text-gradient-primary text-2xl">
              O Gestão de Empréstimo MV foi criado para quem empresta dinheiro e precisa de controle total sem perder tempo com anotações, cobranças manuais ou contas confusas.
            </span>
          </h1>
          <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8"></p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <Link to="/auth"><Button variant="premium" size="lg" className="w-full sm:w-auto">
              Quero Testar <ArrowRight className="w-4 h-4" />
            </Button></Link>
            <a href="#demo"><Button variant="outline" size="lg" className="w-full sm:w-auto">
              Ver Sistema
            </Button></a>
          </div>

          {/* Prova numérica */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
            <ProofCard label="+122 Clientes Ativos" value={`+${fmtNum(s.totalClients)}`} />
            <ProofCard label="Recuperados em Remarketing" value={fmtBRL(s.totalEmprestado)} />
            <ProofCard label="Cobranças Automatizadas" value={fmtNum(s.ativos)} />
            <ProofCard label="em Juros Gerados" value={fmtBRL(s.juros)} />
          </div>
        </div>
      </section>

      {/* Resultados Reais */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Resultados reais da plataforma</h2>
          <p className="text-muted-foreground">Métricas atualizadas direto da nossa base.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatBig icon={Users} label="Clientes gerenciados" value={fmtNum(s.totalClients)} tone="primary" />
          <StatBig icon={DollarSign} label="Total emprestado" value={fmtBRL(s.totalEmprestado)} tone="gold" />
          <StatBig icon={TrendingUp} label="Contratos ativos" value={fmtNum(s.ativos)} tone="primary" />
          <StatBig icon={CheckCircle2} label="Contratos concluídos" value={fmtNum(s.finalizados)} tone="success" />
          <StatBig icon={AlertCircle} label="Alertas monitorados" value={fmtNum(s.vencidos)} tone="danger" />
          <StatBig icon={BarChart3} label="Juros calculados" value={fmtBRL(s.juros)} tone="gold" />
        </div>
      </section>

      {/* Demo */}
      <section id="demo" className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Veja o sistema por dentro</h2>
          <p className="text-muted-foreground">Dashboard, relatórios, central inteligente e detalhes do empréstimo.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <DemoCard title="Dashboard analítico" desc="KPIs, lucro líquido e evolução patrimonial em tempo real." icon={BarChart3} image={dashboardAnaliticoImg} />
          <DemoCard title="Central Inteligente" desc="Cobrança e remarketing automatizados com score." icon={Brain} image={centralInteligenteImg} />
          <DemoCard title="Relatórios financeiros" desc="Fluxo de caixa, juros e movimentações claras." icon={TrendingUp} />
          <DemoCard title="Detalhes do empréstimo" desc="Edite, registre pagamentos e veja histórico completo." icon={DollarSign} />
        </div>
      </section>

      {/* Benefícios */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Tudo que você precisa pra não perder mais dinheiro</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((b) => (
            <Card key={b.title} className="p-5 hover:ring-premium transition-all">
              <CardContent className="p-0">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-3">
                  <b.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">{b.title}</h3>
                <p className="text-sm text-muted-foreground">{b.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Oferta */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <Card className="glass-premium ring-premium max-w-2xl mx-auto p-8 text-center">
          <CardContent className="p-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs mb-4">
              <Crown className="w-3.5 h-3.5" /> Plano PRO
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2">Acesso completo ao sistema</h2>
            <div className="flex items-baseline justify-center gap-2 my-6">
              <span className="text-5xl md:text-6xl font-bold text-gradient-gold">R$ 29,90</span>
              <span className="text-muted-foreground">/mês</span>
            </div>
            <ul className="text-left max-w-sm mx-auto space-y-2 mb-7">
              {[
                "Acesso completo a todas as funções",
                "Clientes e empréstimos ilimitados",
                "Suporte prioritário",
                "Atualizações automáticas",
                "Acesso imediato após pagamento",
                "Cancele quando quiser",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/auth">
              <Button variant="gold" size="lg" className="w-full sm:w-auto">
                Quero Meu Acesso <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">Sem fidelidade. Cancele a qualquer momento.</p>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16 md:py-20 max-w-3xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Perguntas frequentes</h2>
        </div>
        <Card className="p-2 md:p-4">
          <CardContent className="p-2 md:p-4">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </section>

      {/* CTA Final */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-4">
          Comece agora a <span className="text-gradient-primary">organizar seu dinheiro</span>
        </h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
          Acesso imediato. Sem fidelidade. Sistema completo na palma da mão.
        </p>
        <Link to="/auth">
          <Button variant="premium" size="lg">Quero Testar Agora <ArrowRight className="w-4 h-4" /></Button>
        </Link>
      </section>

      <footer className="border-t border-white/[0.05] py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Gestão Inteligente MV. Todos os direitos reservados.
      </footer>
    </div>
  );
};

const ProofCard = ({ label, value }: { label: string; value: string }) => (
  <div className="glass rounded-xl p-3 md:p-4">
    <div className="text-lg md:text-2xl font-bold tabular text-gradient-primary">{value}</div>
    <div className="text-[11px] md:text-xs text-muted-foreground mt-1">{label}</div>
  </div>
);

const StatBig = ({
  icon: Icon, label, value, tone,
}: { icon: any; label: string; value: string; tone: "primary" | "gold" | "success" | "danger" }) => {
  const toneMap = {
    primary: "text-primary bg-primary/10 border-primary/20",
    gold: "text-gold bg-gold/10 border-gold/20",
    success: "text-success bg-success/10 border-success/20",
    danger: "text-destructive bg-destructive/10 border-destructive/20",
  };
  return (
    <Card className="p-5">
      <CardContent className="p-0 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${toneMap[tone]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="text-xl md:text-2xl font-bold tabular truncate">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const DemoCard = ({ title, desc, icon: Icon, image }: { title: string; desc: string; icon: any; image?: string }) => (
  <Card className="p-6 hover:ring-premium transition-all">
    <CardContent className="p-0">
      <div className="aspect-video rounded-xl bg-gradient-to-br from-primary/15 via-accent/10 to-transparent border border-white/[0.05] flex items-center justify-center mb-4 overflow-hidden">
        {image ? (
          <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-16 h-16 text-primary/70" />
        )}
      </div>
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </CardContent>
  </Card>
);

export default Landing;
